import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "Components/PageHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "antd";
import { ArrowLeftOutlined, FileExcelOutlined, LoadingOutlined } from "@ant-design/icons";
import { SbTable, SbTag, SbButton, SbSpin, SbNotification } from "uiCore";
import { getWorkforcePlanDetails } from "Api/apiService";
import {
  PageHeaderStyled,
  HeaderLeftStyled,
  HeaderRightStyled,
  HeadingRowStyled,
  TableWrapperStyled,
} from "styles/common.styles";
import { ClickableTableWrapperStyled } from "../sharedTable.styles";
import { PageTransitionStyled } from "../talentMobility.animations";
import { BackLinkStyled } from "../RoleDetailV2.styles";
import ShortlistTag from "../components/ShortlistTag";
import { IndexInfoLabel } from "../components/IndexTooltip";
import NextRoleMatchTag from "../components/NextRoleMatchTag";
import {
  useRedeploymentCandidates,
  useWorkforcePeopleAvailability,
  useWorkforceDemandCandidates,
} from "../useTalentMobilityData";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import useWorkforcePlanStore from "Store/useWorkforcePlanStore";
import useWorkforcePlanActions from "./useWorkforcePlanActions";
import PlanSaveButton from "../components/PlanSaveButton";
import { exportRowsAsCsv } from "Utils/exportCsv.utils";
import { StepsRowStyled, StepPillStyled, GroupHeadingStyled, SummaryRowStyled } from "./WorkforcePlanningV2.styles";
import {
  WORKFORCE_PLANNING_STEP,
  WORKFORCE_PLANNING_STEPS,
  WORKFORCE_PLAN_CSV_FILENAME,
} from "./WorkforcePlanningV2.constants";
import { buildWorkforcePlanCsvRows } from "./WorkforcePlanningV2.utils";
import { useWorkforcePlanningColumns } from "./useWorkforcePlanningColumns";

import useAnalytics from "Hooks/useAnalytics";
import { CROSS_PAGE_TOUR_PHASE, getTourLegSteps } from "../TalentMobilityManagerV2/TalentMobilityManagerV2.constants";
import useTourStore from "Store/useTourStore";
import SbTourLeg from "uiCore/SbTour/SbTourLeg";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Title, Text } = Typography;

// One matched candidate for the selected demand role — SMI tag plus the jump
// into the Candidate Profile, where the actual shortlisting happens.
const CandidateRow = ({ roleId, match }) => {
  const navigate = useNavigate();
  const thresholds = useMatchThresholds();
  return (
    <SummaryRowStyled style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 24px" }}>
      <span>
        {match.person?.name}
        <span style={{ color: "#9CA3AF", marginLeft: 6 }}>({match.person?.employeeId || "—"})</span>
        <ShortlistTag roleId={roleId} personId={match.personId} />
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <NextRoleMatchTag sri={match.sri} thresholds={thresholds} />
        <SbButton
          buttonType="secondary"
          size="small"
          // flow=workforce tells the Candidate Profile's Shortlist to stay
          // store-only and record the pair on the workforce plan draft.
          onClick={() => navigate(`/talent-mobility/candidate/${match.personId}/role/${roleId}?index=smi&flow=workforce`)}
        >
          View Profile / Shortlist
        </SbButton>
      </span>
    </SummaryRowStyled>
  );
};

const ANALYTICS_FROM = "talent_mobility_workforce_planning";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.WORKFORCE_PLANNING_VIEWED;

const WorkforcePlanningV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  const location = useLocation();
  // Same GET /talent-mobility/employees read (and JD grouping) that feeds
  // Redeployment Center's Role View — API records only, one row per real JD
  // with live occupant counts. The Candidates step fetches each role's
  // people from the people-availability endpoint on drill-in.
  const { roles: fetchedRoles, isLoading } = useRedeploymentCandidates();
  // ── Guided tour: this page's leg of the Talent Mobility tour ───────────────
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const stepsRowRef = useRef(null);
  const handOffTour = (phase, route) => {
    setCrossPageTourPhase(phase);
    navigate(route);
  };

  // Seeded from router state (see goToStep below) so that navigating away —
  // e.g. into a Candidate Profile via "View Profile / Shortlist" — and back
  // via the browser's Back button remounts this page on the same step/role
  // instead of always resetting to Demand.
  const [step, setStep] = useState(location.state?.step || WORKFORCE_PLANNING_STEP.DEMAND);
  const [selectedRoleId, setSelectedRoleId] = useState(location.state?.selectedRoleId || null);
  // A snapshot of the demand row selected into the Candidates step, carried
  // in history state alongside selectedRoleId. Lets the Candidates step
  // render (and its people-availability fetch, keyed only on the id) resume
  // immediately after navigate(-1) from a Candidate Profile, instead of
  // waiting on GET /talent-mobility/employees to reload and re-derive
  // `roles` before `selectedRole` below can resolve.
  const [selectedRoleSnapshot, setSelectedRoleSnapshot] = useState(location.state?.selectedRole || null);

  // Every step change also replaces this page's own history entry with the
  // step/role it's moving to, not just component state. Component state
  // alone doesn't survive navigating away and back (React Router remounts
  // this page fresh on that route), but the history entry's state does —
  // navigate(-1) from a page this component pushed restores it here on
  // mount, above.
  const goToStep = useCallback(
    (nextStep, nextSelectedRoleId = selectedRoleId, nextSelectedRole = selectedRoleSnapshot) => {
      setStep(nextStep);
      setSelectedRoleId(nextSelectedRoleId);
      setSelectedRoleSnapshot(nextSelectedRole);
      navigate(location.pathname, {
        replace: true,
        state: { step: nextStep, selectedRoleId: nextSelectedRoleId, selectedRole: nextSelectedRole },
      });
    },
    [navigate, location.pathname, selectedRoleId, selectedRoleSnapshot]
  );
  const [isLoadingPlan, setIsLoadingPlan] = useState(Boolean(location.state?.loadPlanId));

  // Demand targets live in the shared workforce plan store (keyed by role
  // id) so they survive the round-trip into a Candidate Profile and land on
  // the submit payload's target_positions.
  const targetOverrides = useWorkforcePlanStore((state) => state.targetPositions);
  const setTargetPosition = useWorkforcePlanStore((state) => state.setTargetPosition);
  const shortlistedEmployeeTargets = useWorkforcePlanStore((state) => state.shortlistedEmployeeTargets);
  const getWorkforcePlanPayload = useWorkforcePlanStore((state) => state.getWorkforcePlanPayload);
  const loadPlan = useWorkforcePlanStore((state) => state.loadPlan);

  // Arriving from View Planning's Edit action — it navigates here right away
  // (see ViewPlanningV2's handleEditWorkforcePlan) carrying which plan to
  // load as router state, rather than making the user wait on that page
  // first. Fetch the plan's full draft here instead and show the table's
  // own loading state until it lands. The ref guards against firing twice
  // (React 18 effect double-invoke in dev, or a re-render before navigate's
  // `replace` below clears the state) — loadPlan's reset() would otherwise
  // wipe whatever the user had already started editing. Mirrors
  // RedeploymentCenterV2's identical loadPlanId effect.
  const hasLoadedPlanRef = useRef(false);
  useEffect(() => {
    const loadPlanId = location.state?.loadPlanId;
    if (!loadPlanId || hasLoadedPlanRef.current) return;
    hasLoadedPlanRef.current = true;
    const loadPlanName = location.state?.loadPlanName || "";
    (async () => {
      try {
        const response = await getWorkforcePlanDetails(loadPlanId);
        loadPlan({
          planId: loadPlanId,
          planName: loadPlanName,
          workforcePlans: Array.isArray(response?.workforce_plans) ? response.workforce_plans : [],
        });
      } catch (error) {
        SbNotification.error(
          "Unable to load workforce plan",
          error?.response?.data?.message || "Please try again."
        );
      } finally {
        setIsLoadingPlan(false);
        // Drop the router state so a later re-render, or navigating back to
        // this same history entry, doesn't re-run this load.
        navigate("/talent-mobility/workforce-planning", { replace: true });
      }
    })();
  }, [location.state, loadPlan, navigate]);

  const roles = useMemo(
    () => fetchedRoles.map((r) => ({ ...r, targetPositionCount: targetOverrides[r.id] ?? r.targetPositionCount })),
    [fetchedRoles, targetOverrides]
  );

  // A role is in the plan once its target is raised above current — derived
  // (not click-tracked) so the list survives navigating into a Candidate
  // Profile and back.
  const demandRoles = useMemo(
    () => roles.filter((r) => r.targetPositionCount > r.currentPositionCount),
    [roles]
  );

  // Save Plan enables only once there's an actual demand to save — a
  // session-edited target that's genuinely above Current (role.id in
  // targetOverrides, not just any edited value: typing a target back down
  // to/below Current is still a session edit, but it means "no demand" —
  // see handleTargetChange — so it must NOT count here), or a shortlisted
  // candidate on a demand role. Targets that came pre-set on the fetched
  // roles alone (never session-edited) still don't count, same as before.
  // Also gated on !isLoadingPlan: while a plan is being loaded (see the
  // effect above), a leftover unsaved draft could briefly make this true
  // right before loadPlan's internal reset() replaces it — without the
  // gate, a stray click could submit that stale, about-to-vanish draft.
  const hasDraft = useMemo(
    () =>
      !isLoadingPlan &&
      (demandRoles.some((role) => role.id in targetOverrides) ||
        Object.values(shortlistedEmployeeTargets).some((employeeIds) => employeeIds.length > 0)),
    [demandRoles, targetOverrides, shortlistedEmployeeTargets, isLoadingPlan]
  );

  // Builds the actual submit payload from the on-screen demand list, not
  // just the store's session edits — the store only carries session-edited
  // targets, so a demand row whose target came in with the fetched data
  // (never touched this session) still needs adding here, and one reset
  // back to Current mid-session (still in the store map, meaning "no
  // demand") needs stripping back out. Injected into useWorkforcePlanActions
  // rather than called inline, so the hook stays domain-agnostic.
  const getPayload = useCallback(
    (trimmedPlanName) => {
      const payload = getWorkforcePlanPayload(trimmedPlanName);
      demandRoles.forEach((demandRole) => {
        if (payload.target_positions[demandRole.id] == null) {
          payload.target_positions[demandRole.id] = demandRole.targetPositionCount;
        }
      });
      Object.entries(payload.target_positions).forEach(([jdId, target]) => {
        const role = roles.find((r) => r.id === jdId);
        if (!role || target <= role.currentPositionCount) delete payload.target_positions[jdId];
      });
      return payload;
    },
    [getWorkforcePlanPayload, demandRoles, roles]
  );
  const { isSubmitting, planName, planLabel, isUpdate, savePlan } = useWorkforcePlanActions({
    hasDraft,
    getPayload,
  });

  // A target typed at or below Current is rejected (never stored) and the
  // attempt is remembered here per role, so the explanation shows once on
  // blur — not on every keystroke while a larger number is still half-typed
  // (the "1" of "10" would otherwise trigger it).
  const rejectedTargetRoleIdRef = useRef(null);

  const handleTargetChange = useCallback(
    (role, nextValue) => {
      // InputNumber emits null while the field is cleared mid-edit — ignore it
      // so a half-typed value can't corrupt the demand comparison below.
      if (!Number.isFinite(nextValue)) return;
      // Growth (target > current) drives everything downstream — the Growing
      // tag, the Candidates drill-in, demandRoles, and the submit payload
      // (getPayload strips non-growth targets). A value at or below Current
      // is stored as typed so it clears any earlier raised target, but it
      // is not growth, so the blur warning explains why.
      rejectedTargetRoleIdRef.current = nextValue > role.currentPositionCount ? null : role.id;
      setTargetPosition(role.id, nextValue);
    },
    [setTargetPosition]
  );

  // One shared warning for every "this row isn't growth" moment — blurring a
  // non-growth target entry and clicking a non-growing row. The fixed key
  // keeps the popovers from stacking when both fire on the same click (the
  // input's blur and the row's click).
  const showGrowthRequiredWarning = useCallback((role) => {
    SbNotification.warning(
      "Target must be above Current Positions",
      `Workforce Planning models growth only — set Target Positions above ${role.currentPositionCount} to add demand for "${role.title}". To plan a headcount reduction, use Redeployment Center instead.`,
      { key: "workforce-growth-required" }
    );
  }, []);

  const handleTargetBlur = useCallback(
    (role) => {
      if (rejectedTargetRoleIdRef.current !== role.id) return;
      rejectedTargetRoleIdRef.current = null;
      showGrowthRequiredWarning(role);
    },
    [showGrowthRequiredWarning]
  );

  // Falls back to the history-state snapshot while `roles` (GET
  // /talent-mobility/employees, refetched fresh on this page's remount)
  // hasn't resolved yet — e.g. right after navigate(-1) from a Candidate
  // Profile — so the Candidates step doesn't sit blocked on that call
  // before it can fire the one API it actually needs below.
  const selectedRole =
    roles.find((r) => r.id === selectedRoleId) ||
    (selectedRoleSnapshot?.id === selectedRoleId ? selectedRoleSnapshot : null);
  // GET /talent-mobility/workforce-planning/people-availability for the
  // selected demand role — each candidate carries its `available` flag
  // (mock roles derive it from the session availability store instead).
  const { candidates, isLoading: isAvailabilityLoading } = useWorkforcePeopleAvailability(selectedRole);
  // Largest SMI first in both groups; missing scores sort last instead of crashing the comparator.
  const bySmiDesc = (a, b) => (b.sri ?? -Infinity) - (a.sri ?? -Infinity);
  const availableCandidates = candidates.filter((c) => c.available).sort(bySmiDesc);
  const unavailableCandidates = candidates.filter((c) => !c.available).sort(bySmiDesc);

  // Every demand role's candidates (API roles fetch per role, mock roles
  // resolve from their precomputed lists) — the Summary counts and the CSV
  // export need them all, not just the selected role's. Fetched only once
  // the Summary step opens.
  const { candidatesByRoleId, isLoading: isSummaryLoading } = useWorkforceDemandCandidates(
    demandRoles,
    step === WORKFORCE_PLANNING_STEP.SUMMARY
  );

  // Any of this page's loading states — the Demand table's own fetch, an
  // in-flight Edit-action plan load, the Candidates step's availability
  // fetch, or the Summary step's candidate fetch. Disables View Summary /
  // Save Plan / Save & Close Plan while any of them is active, so a save (or
  // a summary built from as-yet-incomplete data) can't race a still-loading
  // page.
  const isPageBusy = isLoading || isLoadingPlan || isAvailabilityLoading || isSummaryLoading;

  // Only growing rows open their Candidates step — clicking a non-growing
  // row explains why it won't open instead of silently doing nothing.
  const openDemand = (record) => {
    if (record.targetPositionCount <= record.currentPositionCount) {
      showGrowthRequiredWarning(record);
      return;
    }
    goToStep(WORKFORCE_PLANNING_STEP.CANDIDATES, record.id, record);
  };

  const handleExportCsv = () => {
    exportRowsAsCsv(buildWorkforcePlanCsvRows(demandRoles, candidatesByRoleId), WORKFORCE_PLAN_CSV_FILENAME);
  };

  const roleColumns = useWorkforcePlanningColumns({
    roles,
    onTargetChange: handleTargetChange,
    onTargetBlur: handleTargetBlur,
  });

  return (
    <PageTransitionStyled>
      <PageHeader
        title="Workforce Planning"
        titleExtra={
          <>
            <SbTag
              color={isLoadingPlan ? "processing" : isUpdate ? "blue" : "gold"}
              icon={isLoadingPlan ? <LoadingOutlined spin /> : undefined}
              style={{ fontSize: 14 }}
            >
              {isLoadingPlan ? "Plan name..." : planLabel}
            </SbTag>
          </>
        }
        subtitle={
          <>
            <Text type="secondary">
              Strategic Workforce Planning — set demand for a priority role, then find and shortlist internal candidates ranked by <IndexInfoLabel index="SMI" />.
            </Text>
          </>
        }
        onBack={() => navigate("/talent-mobility")}
        backLabel="Back to Talent Mobility"
        actions={
          <>
            <SbButton
              buttonType="secondary"
              disabled={isPageBusy || demandRoles.length === 0}
              onClick={() => goToStep(WORKFORCE_PLANNING_STEP.SUMMARY)}
            >
              View Summary
            </SbButton>
            <PlanSaveButton
              planTypeLabel="Workforce Plan"
              disabled={isPageBusy}
              hasDraft={hasDraft}
              isUpdate={isUpdate}
              planName={planName}
              isSubmitting={isSubmitting}
              onSave={savePlan}
            />
            <PlanSaveButton
              planTypeLabel="Workforce Plan"
              buttonType="secondary"
              closeAfter
              disabled={isPageBusy}
              hasDraft={hasDraft}
              isUpdate={isUpdate}
              planName={planName}
              isSubmitting={isSubmitting}
              onSave={savePlan}
            />
          </>
        }
      />

      <StepsRowStyled ref={stepsRowRef}>
        {WORKFORCE_PLANNING_STEPS.map((s) => (
          <StepPillStyled key={s.key} $active={step === s.key}>{s.label}</StepPillStyled>
        ))}
      </StepsRowStyled>

      {step === WORKFORCE_PLANNING_STEP.DEMAND && (
        <ClickableTableWrapperStyled>
          <SbTable
            columns={roleColumns}
            dataSource={roles}
            loading={isLoading || isLoadingPlan}
            rowKey="id"
            sticky
            onRow={(record) => ({ onClick: () => openDemand(record) })}
          />
        </ClickableTableWrapperStyled>
      )}

      {step === WORKFORCE_PLANNING_STEP.CANDIDATES && (
        selectedRole ? (
          <>
            <PageHeaderStyled>
              <HeaderLeftStyled>
                <BackLinkStyled onClick={() => goToStep(WORKFORCE_PLANNING_STEP.DEMAND)}>
                  <ArrowLeftOutlined /> Back to Demand
                </BackLinkStyled>
                <HeadingRowStyled>
                  <Title level={5}>
                    {selectedRole.title} — Candidates ({selectedRole.currentPositionCount} → {selectedRole.targetPositionCount})
                  </Title>
                </HeadingRowStyled>
              </HeaderLeftStyled>
            </PageHeaderStyled>

            {/* PageTransitionStyled clips overflow, so the role header above
                stays fixed only if the (potentially long) candidate list
                below is its own flex-grow + overflow-y:auto region — same
                TableWrapperStyled the Demand step's table already uses —
                rather than growing past the page's bottom edge unscrollably. */}
            <TableWrapperStyled>
              <SbSpin spinning={isAvailabilityLoading}>
                <GroupHeadingStyled>A. People that are potentially available ({availableCandidates.length})</GroupHeadingStyled>
                {availableCandidates.length ? (
                  availableCandidates.map((c) => <CandidateRow key={c.personId} roleId={selectedRole.id} match={c} />)
                ) : (
                  // Only once the fetch has actually settled — while it's
                  // still in flight, `candidates` defaults to [] and this
                  // would otherwise render "nothing yet" underneath the
                  // spinner as if it were the real, settled answer.
                  !isAvailabilityLoading && (
                    <SummaryRowStyled style={{ padding: "10px 24px" }}>
                      <Text type="secondary">No available candidates yet — mark people available from Redeployment Center.</Text>
                    </SummaryRowStyled>
                  )
                )}

                <GroupHeadingStyled>B. People that are unavailable ({unavailableCandidates.length})</GroupHeadingStyled>
                {unavailableCandidates.length ? (
                  unavailableCandidates.map((c) => <CandidateRow key={c.personId} roleId={selectedRole.id} match={c} />)
                ) : (
                  !isAvailabilityLoading && (
                    <SummaryRowStyled style={{ padding: "10px 24px" }}>
                      <Text type="secondary">No matched candidates for this role yet.</Text>
                    </SummaryRowStyled>
                  )
                )}
              </SbSpin>
            </TableWrapperStyled>
          </>
        ) : (
          // Landing straight on this step (e.g. router-state restore after
          // Back from a Candidate Profile) means the role list itself may
          // still be loading — `selectedRole` can't resolve until it does.
          // Show a spinner instead of a blank panel for that window, same
          // treatment as this page's other full-panel loading states
          // (e.g. RoleDetailV2's own "!role && isLoading" case).
          isLoading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
              <SbSpin size="large" />
            </div>
          )
        )
      )}

      {step === WORKFORCE_PLANNING_STEP.SUMMARY && (
        <>
          <HeaderRightStyled style={{ margin: "0 24px 12px" }}>
            <SbButton
              buttonType="secondary"
              icon={<FileExcelOutlined />}
              disabled={isSummaryLoading}
              onClick={handleExportCsv}
            >
              Export Workforce Plan CSV
            </SbButton>
          </HeaderRightStyled>
          {/* Same reasoning as the Candidates step above — a long demand-role
              list needs its own scroll region, not just PageTransitionStyled's
              clipped overflow. */}
          <TableWrapperStyled style={{ padding: "0 24px" }}>
            <SbSpin spinning={isSummaryLoading}>
              {demandRoles.length ? (
                demandRoles.map((r) => {
                  const roleCandidates = candidatesByRoleId[r.id] ?? [];
                  const availableCount = roleCandidates.filter((c) => c.available).length;
                  return (
                    <SummaryRowStyled key={r.id}>
                      <strong>{r.title}</strong> — Demand {r.currentPositionCount} → {r.targetPositionCount}
                      {" · "}{roleCandidates.length} matched candidate(s), {availableCount} available
                    </SummaryRowStyled>
                  );
                })
              ) : (
                <Text type="secondary">No demand roles set yet — go to Demand and raise a role's Target Positions above Current.</Text>
              )}
            </SbSpin>
          </TableWrapperStyled>
        </>
      )}

      {/* This page's leg of the Talent Mobility tour */}
      <SbTourLeg
        active={crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.WORKFORCE}
        ready={!isLoading && !isLoadingPlan}
        steps={getTourLegSteps(CROSS_PAGE_TOUR_PHASE.WORKFORCE)}
        resolveTarget={() => stepsRowRef.current}
        onNext={() => handOffTour(CROSS_PAGE_TOUR_PHASE.VIEW_PLANS, "/talent-mobility/view-planning")}
        onClose={() => handOffTour(CROSS_PAGE_TOUR_PHASE.FINISH, "/talent-mobility")}
      />
    </PageTransitionStyled>
  );
};

export default WorkforcePlanningV2;
